import { RPCClient } from '@buffer-mono/legacy-rpc-utils'
import type { RpcCaller } from '@buffer-mono/legacy-rpc-utils'

import { actions, actionTypes } from './reducer'

interface MiddlewareAction {
  type: string
  name?: string
  args?: unknown
}

interface MiddlewareStore {
  dispatch: (action: MiddlewareAction) => void
  getState?: () => unknown
}

type NextDispatch = (action: MiddlewareAction) => void

const stringifyError = (error: unknown): string => {
  if (error instanceof Error) return error.message
  if (typeof error === 'object' && error !== null) {
    try {
      return JSON.stringify(error)
    } catch {
      return String(error)
    }
  }
  return String(error)
}

export const middleware =
  (rpcClient?: RpcCaller) =>
  (
    store: MiddlewareStore,
  ): ((next: NextDispatch) => (action: MiddlewareAction) => void) => {
    let counter = 0
    const rpc: RpcCaller =
      rpcClient ??
      new RPCClient({
        url: '/rpc',
        sendCredentials: 'same-origin',
      })

    return (next: NextDispatch) =>
      (action: MiddlewareAction): void => {
        next(action)
        switch (action.type) {
          case actionTypes.FETCH: {
            // FETCH actions are produced by `actions.fetch(...)` which always
            // sets `name`; bail out if a caller dispatches a bare FETCH so we
            // don't propagate an undefined name into the store or RPC layer.
            const { name } = action
            if (typeof name !== 'string') break

            const id = counter++
            const args = action.args ?? {}

            store.dispatch(
              actions.fetchStart({
                name,
                args,
                id,
              }),
            )

            const clientId = window.xBufferClientId || 'unknown-frontend'
            // Vite sometimes emits the literal string 'undefined' for an unset
            // env var; treat it as absent so the header value stays meaningful.
            const clientVersion =
              window.xBufferClientVersion &&
              window.xBufferClientVersion !== 'undefined'
                ? window.xBufferClientVersion
                : 'unknown'
            rpc
              .call(name, args, {
                'x-buffer-client-id': clientId,
                'x-buffer-client-name': clientId,
                'x-buffer-client-version': clientVersion,
              })
              .then((result) =>
                store.dispatch(
                  actions.fetchSuccess({
                    name,
                    args,
                    id,
                    result,
                  }),
                ),
              )
              .catch((error: unknown) => {
                // Preserved for production debugging; the dispatched error
                // only carries a string message and loses the original stack.
                // oxlint-disable-next-line no-console
                console.log('error', error)
                store.dispatch(
                  actions.fetchFail({
                    name,
                    args,
                    id,
                    error: stringifyError(error),
                  }),
                )
              })
            break
          }
          default:
            break
        }
      }
  }
